import { FC, useState } from "react";
import { Button } from "src/components/ui/button";
import { useAreas, useRegions, useStores } from "src/features/reporting/hooks";
import YearFilter from "../YearFilter/YearFilter";
import EmployeeSearch from "../EmployeeSearch/EmployeeSearch";
import StoreSearch from "../StoreSearch/StoreSearch";
import { checkObservationFormType } from "src/utils/checkObservationFormType";
import dayjs from "dayjs";

type ValuesState = (value: any) => void;

interface SetValuesState {
	setActiveRegion: ValuesState;
	setActiveArea: ValuesState;
	setActiveStore: ValuesState;
	setActiveYear: ValuesState;
}

export interface LocationFilterProps {
	setValues: SetValuesState;
	resetFilters: () => void;
	selectedRegion?: number | null;
	selectedArea?: number | null;
	selectedStore?: number | null;
	selectedYear?: number | undefined;
	showIncludeYear: boolean;
	years?: number[] | undefined;
	isRegionDisabled: boolean;
	isAreaDisabled: boolean;
	isStoreDisabled: boolean;
	observationType?: IObservationType;
	onEmployeeSelect?: (employee: {
		store?: {
			id: number;
			name: string;
			region?: { id: number; name: string } | null;
			area?: { id: number; name: string } | null;
		} | null;
	}) => void;
	onStoreSelect?: (store: {
		id: number;
		name: string;
		region?: number;
		area?: number;
	}) => void;
}

const LocationFilter: FC<LocationFilterProps> = ({
	setValues,
	resetFilters,
	selectedArea,
	selectedRegion,
	selectedStore,
	selectedYear = undefined,
	showIncludeYear = true,
	years,
	isRegionDisabled,
	isAreaDisabled,
	isStoreDisabled,
	observationType,
	onEmployeeSelect,
	onStoreSelect
}) => {
	const isStoreBasedForm =
		checkObservationFormType(observationType) === "store";
	const [storeSearchResetKey, setStoreSearchResetKey] = useState(0);
	const { data: regions } = useRegions();
	const { data: areas } = useAreas();
	const { data: stores } = useStores();

	const isResetButtonDisabled =
		!selectedRegion &&
		!selectedArea &&
		!selectedStore &&
		selectedYear === dayjs().year();

	const selectClasses =
		"w-full px-3 py-2.5 bg-white border border-border rounded-lg text-base font-medium focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

	return (
		<div
			className="bg-white border border-border rounded-xl p-4 shadow-xs"
			data-testid="component.LocationFilter"
		>
			{isStoreBasedForm && onStoreSelect ? (
				<div className="mb-3">
					<StoreSearch
						onSelect={onStoreSelect}
						resetKey={storeSearchResetKey}
					/>
				</div>
			) : (
				onEmployeeSelect && (
					<div className="mb-3">
						<EmployeeSearch onSelect={onEmployeeSelect} />
					</div>
				)
			)}

			<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_auto_auto] gap-3 items-center">
				<select
					data-testid="region-picker"
					className={selectClasses}
					disabled={isRegionDisabled}
					value={selectedRegion?.toString() ?? ""}
					onChange={(e) => {
						setValues.setActiveRegion(
							e.target.value === "" ? undefined : Number(e.target.value)
						);
						setValues.setActiveArea(undefined);
						setValues.setActiveStore(undefined);
					}}
				>
					<option value="">All Regions</option>

					{regions &&
						regions.results.map((region) => (
							<option key={region.id} value={region.id}>
								{region.name}
							</option>
						))}
				</select>

				<select
					data-testid="area-picker"
					className={selectClasses}
					disabled={isAreaDisabled || !selectedRegion}
					value={selectedArea?.toString() ?? ""}
					onChange={(e) => {
						setValues.setActiveArea(
							e.target.value === "" ? undefined : Number(e.target.value)
						);
						setValues.setActiveStore(undefined);
					}}
				>
					<option value="">All Areas</option>

					{areas &&
						areas.results
							.filter(
								(area) => !selectedRegion || area.region === selectedRegion
							)
							.map((area) => (
								<option key={area.id} value={area.id}>
									{area.name}
								</option>
							))}
				</select>

				<select
					data-testid="store-picker"
					className={selectClasses}
					disabled={isStoreDisabled || !selectedArea}
					value={selectedStore?.toString() ?? ""}
					onChange={(e) =>
						setValues.setActiveStore(
							e.target.value === "" ? undefined : Number(e.target.value)
						)
					}
				>
					<option value="">All Stores</option>

					{stores &&
						stores.results
							.filter((store) => !selectedArea || store.area === selectedArea)
							.map((store) => (
								<option key={store.id} value={store.id}>
									{store.name}
								</option>
							))}
				</select>

				{showIncludeYear && (
					<YearFilter
						yearValue={selectedYear}
						years={years ?? []}
						setYearValue={setValues.setActiveYear}
					/>
				)}

				<Button
					variant="outline"
					onClick={() => {
						resetFilters();
						setStoreSearchResetKey((k) => k + 1);
					}}
					data-testid="reset-btn"
					disabled={isResetButtonDisabled}
					className="w-full xl:w-auto"
				>
					Reset All
				</Button>
			</div>
		</div>
	);
};

export default LocationFilter;
